import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/itemsTransferAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// Builds the depreciation-percent dropdown options from a MaxPercent/CentralStoreCC
// pair, ported 1:1 from PurchaseController.ItemsTransferCSK (legacy MVC app) so the
// same CC always sees the same percent list. `cccode` is the transferring (From) CC —
// when it IS the configured central store, percentages count up to the max (plus a
// "Full Value" = 100 option); otherwise they count down from the max to 100.
const buildDepPercentList = (maxPercent, centralStoreCC, cccode) => {
    if (!(maxPercent > 0)) return [];
    const no = Math.floor(maxPercent / 10);
    const list = [];
    if (cccode === centralStoreCC) {
        if (no === 10) {
            for (let i = 1; i < no; i++) list.push({ Value: i * 10, Name: String(i * 10) });
            list.push({ Value: 100, Name: 'FullValue' });
        } else {
            for (let i = 1; i <= no; i++) list.push({ Value: i * 10, Name: String(i * 10) });
        }
    } else {
        const max = 10;
        if (no === 10) {
            list.push({ Value: 100, Name: 'FullValue' });
        } else {
            for (let i = no; i <= max; i++) list.push({ Value: i * 10, Name: String(i * 10) });
        }
    }
    return list;
};

// ── Async Thunks ─────────────────────────────────────────────────────────────

// Resolves whether the caller is a plain Store Keeper (SK) or the Central Store
// Keeper (CSK) for the StockTransfer workflow — IndentPresentLevel === IndentDefineLevel
// means CSK, which drives the whole page into the ToCC + depreciation flow.
export const fetchLevels = createAsyncThunk(
    'itemsTransfer/fetchLevels',
    async (roleId, { rejectWithValue }) => {
        try {
            const res = await api.getIndentLevelsIT(roleId);
            return res?.Data || {};
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to determine workflow level'));
        }
    }
);

export const fetchFromCCList = createAsyncThunk(
    'itemsTransfer/fetchFromCCList',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getFromCCList(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchToCCList = createAsyncThunk(
    'itemsTransfer/fetchToCCList',
    async ({ uid, rid, cc }, { rejectWithValue }) => {
        try {
            const res = await api.getToCCList(uid, rid, cc);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load receiving cost centers'));
        }
    }
);

export const fetchCategoryList = createAsyncThunk(
    'itemsTransfer/fetchCategoryList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getItemCategories();
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load categories'));
        }
    }
);

// isCSK picks which staged-items endpoint backs the grid: the CSK variant additionally
// carries Basic price (used client-side for Before/After Depreciation).
export const fetchGrid = createAsyncThunk(
    'itemsTransfer/fetchGrid',
    async ({ cc, rid, isCSK }, { rejectWithValue }) => {
        try {
            const res = isCSK
                ? await api.getItemTransferDetailscsk(cc, rid)
                : await api.getItemTransferDetails(cc, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load staged transfer items'));
        }
    }
);

export const fetchSuggestions = createAsyncThunk(
    'itemsTransfer/fetchSuggestions',
    async ({ pfx, typ, cat, cc }, { rejectWithValue }) => {
        try {
            const res = await api.getAutoCompleteItemTransfer(pfx, typ, cat, cc);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load suggestions'));
        }
    }
);

// Probes with ToCC = 'CC00' (a placeholder — see buildDepPercentList) exactly like the
// legacy ItemsTransferCSK action, independent of whichever real ToCC the user later picks.
export const fetchDepPercent = createAsyncThunk(
    'itemsTransfer/fetchDepPercent',
    async ({ fromCC }, { rejectWithValue }) => {
        try {
            const res = await api.getItemDepPercentbyCC(fromCC, 'CC00');
            const data = res?.Data || {};
            const maxPercent = data.MaxPercent || 0;
            const centralStoreCC = data.CentralStoreCC || null;
            return {
                list: buildDepPercentList(maxPercent, centralStoreCC, fromCC),
                exist: maxPercent > 0,
                centralStoreCC,
            };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load depreciation options'));
        }
    }
);

// Success format from the SP is "Submited" — anything else is an error message shown verbatim.
export const addItem = createAsyncThunk(
    'itemsTransfer/addItem',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveItemsTransferItems(payload);
            const raw = res?.Data || '';
            if (raw.split(',')[0]?.trim() !== 'Submited') {
                return rejectWithValue(raw || 'Failed to add item');
            }
            return { code: payload.ItemCode };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add item'));
        }
    }
);

export const deleteItem = createAsyncThunk(
    'itemsTransfer/deleteItem',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.deleteItemsTransferItem(payload);
            const status = res?.Data || '';
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to delete item');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to delete item'));
        }
    }
);

// Success format is "Submited,<RefNo>" for both the SK and CSK submit endpoints.
export const submitTransfer = createAsyncThunk(
    'itemsTransfer/submitTransfer',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveItemsTransfer(payload);
            const raw = res?.Data || '';
            const [status, refNo] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit transfer');
            }
            return { refNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit transfer'));
        }
    }
);

export const submitCSKTransfer = createAsyncThunk(
    'itemsTransfer/submitCSKTransfer',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveCSKItemsTransfer(payload);
            const raw = res?.Data || '';
            const [status, refNo] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit transfer');
            }
            return { refNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit transfer'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    presentLevel: null,
    defineLevel: null,
    fromCCList: [],
    toCCList: [],
    categoryList: [],
    gridItems: [],
    suggestions: [],
    depPercentList: [],
    depPercentExist: false,
    centralStoreCC: null,
    addResult: null,
    submitResult: null,
    loading: {
        levels: false,
        fromCC: false,
        toCC: false,
        categoryList: false,
        grid: false,
        suggestions: false,
        depPercent: false,
        add: false,
        deleteItem: false,
        submit: false,
    },
    errors: {
        levels: null,
        fromCC: null,
        toCC: null,
        categoryList: null,
        grid: null,
        depPercent: null,
        add: null,
        deleteItem: null,
        submit: null,
    },
};

const itemsTransferSlice = createSlice({
    name: 'itemsTransfer',
    initialState,
    reducers: {
        clearToCCList:    (s) => { s.toCCList = []; },
        clearGrid:        (s) => { s.gridItems = []; s.errors.grid = null; },
        clearSuggestions: (s) => { s.suggestions = []; },
        clearDepPercent:  (s) => { s.depPercentList = []; s.depPercentExist = false; s.centralStoreCC = null; },
        clearAddResult:   (s) => { s.addResult = null; s.errors.add = null; },
        clearSubmitResult:(s) => { s.submitResult = null; s.errors.submit = null; },
        resetItemsTransfer: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLevels.pending,   (s) => { s.loading.levels = true; s.errors.levels = null; })
            .addCase(fetchLevels.fulfilled, (s, a) => {
                s.loading.levels = false;
                s.presentLevel = a.payload.IndentPresentLevel ?? null;
                s.defineLevel = a.payload.IndentDefineLevel ?? null;
            })
            .addCase(fetchLevels.rejected,  (s, a) => { s.loading.levels = false; s.errors.levels = a.payload; });

        builder
            .addCase(fetchFromCCList.pending,   (s) => { s.loading.fromCC = true; s.errors.fromCC = null; })
            .addCase(fetchFromCCList.fulfilled, (s, a) => { s.loading.fromCC = false; s.fromCCList = a.payload; })
            .addCase(fetchFromCCList.rejected,  (s, a) => { s.loading.fromCC = false; s.errors.fromCC = a.payload; });

        builder
            .addCase(fetchToCCList.pending,   (s) => { s.loading.toCC = true; s.errors.toCC = null; })
            .addCase(fetchToCCList.fulfilled, (s, a) => { s.loading.toCC = false; s.toCCList = a.payload; })
            .addCase(fetchToCCList.rejected,  (s, a) => { s.loading.toCC = false; s.errors.toCC = a.payload; });

        builder
            .addCase(fetchCategoryList.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchCategoryList.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchCategoryList.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(fetchGrid.pending,   (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridItems = a.payload; })
            .addCase(fetchGrid.rejected,  (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; s.gridItems = []; });

        builder
            .addCase(fetchSuggestions.pending,   (s) => { s.loading.suggestions = true; })
            .addCase(fetchSuggestions.fulfilled, (s, a) => { s.loading.suggestions = false; s.suggestions = a.payload; })
            .addCase(fetchSuggestions.rejected,  (s) => { s.loading.suggestions = false; s.suggestions = []; });

        builder
            .addCase(fetchDepPercent.pending,   (s) => { s.loading.depPercent = true; s.errors.depPercent = null; })
            .addCase(fetchDepPercent.fulfilled, (s, a) => {
                s.loading.depPercent = false;
                s.depPercentList = a.payload.list;
                s.depPercentExist = a.payload.exist;
                s.centralStoreCC = a.payload.centralStoreCC;
            })
            .addCase(fetchDepPercent.rejected,  (s, a) => { s.loading.depPercent = false; s.errors.depPercent = a.payload; });

        builder
            .addCase(addItem.pending,   (s) => { s.loading.add = true; s.errors.add = null; s.addResult = null; })
            .addCase(addItem.fulfilled, (s, a) => { s.loading.add = false; s.addResult = a.payload; })
            .addCase(addItem.rejected,  (s, a) => { s.loading.add = false; s.errors.add = a.payload; });

        builder
            .addCase(deleteItem.pending,   (s) => { s.loading.deleteItem = true; s.errors.deleteItem = null; })
            .addCase(deleteItem.fulfilled, (s) => { s.loading.deleteItem = false; })
            .addCase(deleteItem.rejected,  (s, a) => { s.loading.deleteItem = false; s.errors.deleteItem = a.payload; });

        builder
            .addCase(submitTransfer.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitTransfer.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.gridItems = []; })
            .addCase(submitTransfer.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });

        builder
            .addCase(submitCSKTransfer.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitCSKTransfer.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.gridItems = []; })
            .addCase(submitCSKTransfer.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearToCCList,
    clearGrid,
    clearSuggestions,
    clearDepPercent,
    clearAddResult,
    clearSubmitResult,
    resetItemsTransfer,
} = itemsTransferSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectITPresentLevel  = (s) => s.itemsTransfer.presentLevel;
export const selectITDefineLevel   = (s) => s.itemsTransfer.defineLevel;
export const selectITIsCSK         = (s) => s.itemsTransfer.presentLevel !== null &&
    s.itemsTransfer.presentLevel === s.itemsTransfer.defineLevel;
export const selectITFromCCList    = (s) => s.itemsTransfer.fromCCList;
export const selectITToCCList      = (s) => s.itemsTransfer.toCCList;
export const selectITCategoryList  = (s) => s.itemsTransfer.categoryList;
export const selectITGridItems     = (s) => s.itemsTransfer.gridItems;
export const selectITSuggestions   = (s) => s.itemsTransfer.suggestions;
export const selectITDepPercentList  = (s) => s.itemsTransfer.depPercentList;
export const selectITDepPercentExist = (s) => s.itemsTransfer.depPercentExist;
export const selectITCentralStoreCC  = (s) => s.itemsTransfer.centralStoreCC;
export const selectITAddResult     = (s) => s.itemsTransfer.addResult;
export const selectITSubmitResult  = (s) => s.itemsTransfer.submitResult;
export const selectITLoading       = (s) => s.itemsTransfer.loading;
export const selectITErrors        = (s) => s.itemsTransfer.errors;

export default itemsTransferSlice.reducer;
